import React, { useEffect, useState, useCallback } from "react";
import { adminApi, formatApiError } from "../../../context/AuthContext";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Alert, AlertDescription } from "../../ui/alert";
import { RefreshCw, Info, CheckCircle2, AlertTriangle, XCircle, Pencil } from "lucide-react";
import ColliMetricsBadge from "./ColliMetricsBadge";

/**
 * QuoteMappingReview — toont de mapping-report na quote→shipment conversie.
 *
 * REGEL 2: leest UITSLUITEND uit backend endpoint
 *   `GET /api/admin/shipments/{shipment_id}/mapping-report`
 *
 * Toont per veld de mapping-status (overgenomen/ontbreekt/controle_nodig/
 * handmatig_aangepast) + pricing-facade banner met eerlijke labels.
 */

const STATUS_META = {
  overgenomen: {
    label: "Overgenomen",
    icon: CheckCircle2,
    className: "bg-green-100 text-green-800 border-green-300",
  },
  ontbreekt: {
    label: "Ontbreekt",
    icon: XCircle,
    className: "bg-red-100 text-red-800 border-red-300",
  },
  controle_nodig: {
    label: "Controle nodig",
    icon: AlertTriangle,
    className: "bg-amber-100 text-amber-800 border-amber-300",
  },
  handmatig_aangepast: {
    label: "Handmatig aangepast",
    icon: Pencil,
    className: "bg-blue-100 text-blue-800 border-blue-300",
  },
};

const FIELD_LABELS = {
  customer_id: "Klant",
  sender: "Verzender",
  recipient: "Ontvanger",
  destination: "Bestemming",
  destination_country_code: "Bestemmingsland",
  service_type: "Diensttype",
  transport_mode: "Transport-modus",
  items: "Colli / artikelen",
  piece_count: "Aantal colli",
  weight_kg: "Werkelijk gewicht",
  cbm: "CBM",
  volumetric_weight_kg: "Volumetrisch gewicht",
  chargeable_weight_kg: "Chargeable weight",
  value: "Waarde",
  currency: "Valuta",
};

export default function QuoteMappingReview({ shipmentId, initialData = null, onRefresh }) {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!shipmentId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await adminApi.get(`/admin/shipments/${shipmentId}/mapping-report`);
      setReport(r.data || null);
    } catch (e) {
      setError(formatApiError(e));
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [shipmentId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !initialData) {
    return (
      <div
        className="rounded-lg border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500"
        data-testid="mapping-review-loading"
      >
        Mapping-rapport wordt geladen…
      </div>
    );
  }

  if (error && !initialData) {
    return (
      <Alert className="border-red-300 bg-red-50" data-testid="mapping-review-error">
        <XCircle className="h-4 w-4 text-red-700" />
        <AlertDescription className="text-red-900 text-sm">{error}</AlertDescription>
      </Alert>
    );
  }

  if (!report && !initialData) return null;

  // Data-bronvolgorde: mapping-report endpoint eerst (vers), fallback naar
  // initialData uit convert-to-shipment response (bevat pricing + metrics inline).
  // BELANGRIJK: expliciete merge — report-velden overschrijven initialData,
  // maar initialData-EXCLUSIEVE velden (pricing_facade_snapshot, colli_totals)
  // blijven behouden wanneer het mapping-report endpoint deze niet levert.
  const src = { ...(initialData || {}), ...(report || {}) };
  // Fields: backend levert {mapping_report:{fields:[...]}} of {fields:[...]}.
  const fields =
    src.mapping_report?.fields || src.fields || src.mapping || [];
  // Pricing en metrics — beide bronnen zijn nu bereikbaar via de merge.
  const pricing = src.pricing_facade_snapshot || src.pricing || null;
  const metrics = src.colli_totals || src.metrics || src.totals || null;

  const controleNodig = fields.filter((f) => f.status === "controle_nodig");
  const ontbrekend = fields.filter((f) => f.status === "ontbreekt");
  const overgenomen = fields.filter((f) => f.status === "overgenomen");
  const handmatig = fields.filter((f) => f.status === "handmatig_aangepast");

  return (
    <div className="space-y-4" data-testid="mapping-review-root">
      {/* Header + refresh */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-slate-900">Mapping-controle</h3>
          <p className="text-xs text-slate-500">
            Rapport uit backend `mapping-report` endpoint — één bron van waarheid
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={load}
          disabled={loading}
          data-testid="mapping-review-refresh"
        >
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Ververs
        </Button>
      </div>

      {/* Samenvatting */}
      <div className="grid grid-cols-4 gap-2 text-xs">
        <SummaryPill
          label="Overgenomen"
          count={overgenomen.length}
          className="bg-green-50 border-green-200 text-green-800"
          testId="summary-overgenomen"
        />
        <SummaryPill
          label="Controle nodig"
          count={controleNodig.length}
          className="bg-amber-50 border-amber-200 text-amber-800"
          testId="summary-controle"
        />
        <SummaryPill
          label="Ontbreekt"
          count={ontbrekend.length}
          className="bg-red-50 border-red-200 text-red-800"
          testId="summary-ontbreekt"
        />
        <SummaryPill
          label="Handmatig"
          count={handmatig.length}
          className="bg-blue-50 border-blue-200 text-blue-800"
          testId="summary-handmatig"
        />
      </div>

      {/* Colli metrics */}
      {metrics && (
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-slate-600 mb-1">
            Aggregaten (uit backend)
          </div>
          <ColliMetricsBadge metrics={metrics} testId="mapping-review-metrics" />
        </div>
      )}

      {/* Pricing facade banner */}
      {pricing && <PricingFacadeBanner pricing={pricing} />}

      {/* Veldlijst */}
      {fields.length > 0 && (
        <div className="rounded-lg border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-xs uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">Veld</th>
                <th className="px-3 py-2 text-left">Waarde</th>
                <th className="px-3 py-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {fields.map((f, idx) => (
                <FieldRow key={f.field || idx} field={f} testId={`mapping-field-${idx}`} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {onRefresh && (
        <div className="flex justify-end">
          <Button size="sm" variant="ghost" onClick={onRefresh} data-testid="mapping-parent-refresh">
            Herlaad zending
          </Button>
        </div>
      )}
    </div>
  );
}

function SummaryPill({ label, count, className, testId }) {
  return (
    <div
      className={`rounded-md border px-2 py-1.5 text-center ${className}`}
      data-testid={testId}
    >
      <div className="text-lg font-semibold leading-none">{count}</div>
      <div className="text-[10px] uppercase mt-0.5">{label}</div>
    </div>
  );
}

function FieldRow({ field, testId }) {
  const meta = STATUS_META[field.status] || STATUS_META.overgenomen;
  const Icon = meta.icon;
  const label = FIELD_LABELS[field.field] || field.field;
  const value = field.source_value ?? field.value ?? "—";
  const displayValue =
    typeof value === "object" ? JSON.stringify(value) : String(value);
  return (
    <tr data-testid={testId}>
      <td className="px-3 py-2 font-medium text-slate-900">{label}</td>
      <td className="px-3 py-2 text-slate-700 max-w-[300px] truncate" title={displayValue}>
        {displayValue}
      </td>
      <td className="px-3 py-2">
        <Badge
          variant="outline"
          className={`${meta.className} border text-xs`}
          data-testid={`${testId}-status`}
        >
          <Icon className="h-3 w-3 mr-1" />
          {meta.label}
        </Badge>
        {field.note && (
          <div className="text-[11px] text-slate-500 mt-0.5">{field.note}</div>
        )}
      </td>
    </tr>
  );
}

function PricingFacadeBanner({ pricing }) {
  const {
    pricing_engine_status,
    pricing_source,
    quoted_price_snapshot_total,
    currency,
    override_baseline,
    override_reason,
  } = pricing || {};

  const isEngineCalculated = pricing_engine_status === "engine_calculated";
  const isSnapshotOnly = pricing_engine_status === "snapshot_only";
  const isNotAvailable = pricing_engine_status === "not_available";

  return (
    <Alert
      className={
        isEngineCalculated
          ? "border-green-300 bg-green-50"
          : isSnapshotOnly
          ? "border-amber-300 bg-amber-50"
          : "border-slate-300 bg-slate-50"
      }
      data-testid="pricing-facade-banner"
    >
      <Info
        className={
          isEngineCalculated
            ? "h-4 w-4 text-green-700"
            : isSnapshotOnly
            ? "h-4 w-4 text-amber-700"
            : "h-4 w-4 text-slate-700"
        }
      />
      <AlertDescription className="text-sm space-y-1">
        <div className="font-semibold" data-testid="pricing-engine-status">
          Prijsberekening:{" "}
          {isEngineCalculated
            ? "berekend door tariff-engine"
            : isSnapshotOnly
            ? "snapshot uit offerte (geen route-tariff)"
            : "niet beschikbaar"}
        </div>
        <div className="text-xs text-slate-600">
          Bron: <code className="text-[11px]">{pricing_source || "onbekend"}</code>
        </div>
        {typeof quoted_price_snapshot_total === "number" && (
          <div data-testid="quoted-price-snapshot">
            <strong>Offerte-snapshot:</strong>{" "}
            {currency || "EUR"}{" "}
            {quoted_price_snapshot_total.toLocaleString("nl-NL", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </div>
        )}
        {typeof override_baseline === "number" && override_baseline > 0 && (
          <div className="text-xs" data-testid="pricing-override-baseline">
            Handmatige override — baseline was{" "}
            {currency || "EUR"}{" "}
            {override_baseline.toLocaleString("nl-NL", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
            {override_reason ? ` — reden: ${override_reason}` : ""}
          </div>
        )}
        {isSnapshotOnly && (
          <div className="text-xs italic text-amber-800">
            Let op: dit is snapshot-arithmetiek uit de offerte, geen route-gebaseerde
            tarifering. Definitieve prijs wordt bepaald door de tariff-engine (nog niet actief).
          </div>
        )}
      </AlertDescription>
    </Alert>
  );
}
